import { useContext } from 'react';
import { Shop, ValidatedCartSubItem } from '@water-web/types';

import { CartContext } from '../../contexts';

export type PaymentSettings = {
  requiresPrepayment: boolean;
  requiresPartialDeposits: boolean;
  allowInPersonPayments?: boolean;
  requestCardForInPersonPayments?: boolean;
  depositType: 'percentage' | 'amount' | null;
  depositPercentage: number | null;
  depositAmount: number | null;
  disclaimer?: string;
};

const getBaseShopSettings = (shop: Shop) => {
  const settings = {
    requiresPrepayment: shop.requiresPrepayment || false,
    requiresPartialDeposits: shop.requiresPartialDeposits || false,
    allowInPersonPayments: shop.allowInPersonPayments || false,
    requestCardForInPersonPayments: shop.requestCardForInPersonPayments || false,
    depositType: shop.settings?.depositType || null,
    depositPercentage: shop.settings?.depositPercentage || null,
    depositAmount: shop.settings?.depositAmount || null,
  };

  return settings;
};

const applyServiceOverrides = (baseSettings: PaymentSettings, services: ValidatedCartSubItem[]) => {
  const settings = { ...baseSettings };

  const hasServiceRequiringPrepayment = services.some((service) => service.requiresPrepaid);
  const hasServiceRequiringPartialDeposits = services.some((service) => service.requiresDeposit);

  if (hasServiceRequiringPrepayment) {
    settings.requiresPrepayment = true;
    settings.requiresPartialDeposits = false;
    return { settings, serviceOverride: true };
  }

  if (hasServiceRequiringPartialDeposits && !settings.requiresPrepayment) {
    settings.requiresPartialDeposits = true;
    const serviceWithDeposit = services.find((service) => service.requiresDeposit);
    if (serviceWithDeposit) {
      settings.depositType = serviceWithDeposit.depositType || settings.depositType;
      settings.depositPercentage = serviceWithDeposit.depositPercentage || settings.depositPercentage;
      settings.depositAmount = serviceWithDeposit.depositAmount || settings.depositAmount;
    }
    return { settings, serviceOverride: true };
  }

  return { settings, serviceOverride: false };
};

const getDefaultSettings = () => ({
  requiresPrepayment: false,
  requiresPartialDeposits: false,
  allowInPersonPayments: false,
  requestCardForInPersonPayments: false,
  depositType: null,
  depositPercentage: null,
  depositAmount: null,
  disclaimer: null,
});

export const usePaymentSettings = () => {
  const { cart } = useContext(CartContext);
  const cartValues = cart.getValidatedCart()?.getDataValues();

  if (!cartValues) {
    return getDefaultSettings();
  }

  const { shop } = cartValues;
  let services = (cartValues.items ?? [])
    .flatMap((item) => item.subItems ?? [])
    .filter((subitem) => subitem.type === 'service');

  if (services.length === 0) {
    const activeReservation = cart.getActiveReservation();
    const service = activeReservation.getService()?.getDataValues();
    if (service) {
      services = [
        {
          requiresPrepaid: service.requiresPrepaid,
          requiresDeposit: service.requiresDeposit,
          depositAmount: service.depositAmount,
          depositPercentage: service.depositPercentage,
          depositType: service.depositType,
        } as ValidatedCartSubItem,
      ];
    }
  }

  if (!shop) {
    return getDefaultSettings();
  }

  const baseSettings = getBaseShopSettings(shop);
  const { settings } = applyServiceOverrides(baseSettings, services);

  return settings;
};
